/**
 * Single source of truth for coloring-book pricing shown in the App.
 *
 * NOTE: these values are for display only. The amount actually charged is set
 * by Shopify — the cart hands off to the `/pages/secure-checkout` bridge with
 * variant IDs + quantities and Shopify prices the line items. Keep these in
 * sync with the Shopify product prices and the tier discount on the store.
 */

/** Price of the first book. */
export const BASE_BOOK_PRICE = 24.95;

/** Price of every book after the first. */
export const ADDITIONAL_BOOK_PRICE = 10;

/**
 * Price of the PDF-only download (the `?tag=downloadPdf` flow).
 *
 * Display only — that flow reads the live price off the Shopify product, so
 * this is used solely for "value" copy. Keep in sync with the Shopify product.
 */
export const PDF_DOWNLOAD_PRICE = 9.95;

/**
 * Price of the Printable PDF Add-On offered per book on Finalize Order, shown
 * next to PDF_DOWNLOAD_PRICE struck through. Display only — Shopify charges the
 * "Printable PDF Add-On" product's own price. Keep in sync with that product.
 */
export const PDF_ADDON_PRICE = 5;

/** Order size at which the flat bulk discount replaces the per-book ladder. */
export const BULK_DISCOUNT_MIN_QUANTITY = 10;

/** Percentage off full list price (qty x BASE_BOOK_PRICE) at bulk quantities. */
export const BULK_DISCOUNT_PERCENT = 55;

const roundToCents = (amount) => Number(amount.toFixed(2));

/** True when the order qualifies for the flat bulk discount. */
export const isBulkDiscounted = (totalQuantity) =>
  (Number(totalQuantity) || 0) >= BULK_DISCOUNT_MIN_QUANTITY;

/**
 * Total price for `totalQuantity` books, across ALL books in the order.
 *
 * Below BULK_DISCOUNT_MIN_QUANTITY: first book is BASE_BOOK_PRICE and each
 * additional book is ADDITIONAL_BOOK_PRICE.
 *
 * At or above it: BULK_DISCOUNT_PERCENT off full list price, which REPLACES the
 * ladder rather than stacking on it.
 *
 * Why the shape changes at 10: Shopify discounts take a fixed $-off amount per
 * quantity, so the ladder has to be enumerated one discount per quantity. Those
 * exist for 2-9; past that a single flat percentage stands in for the rest.
 * 55% is the closest round cap to where the ladder actually sits there (the
 * ladder is ~53.9% off at 10 books and ~55.3% off at 13).
 *
 * Consequence: an extra book costs $11.23 rather than ADDITIONAL_BOOK_PRICE
 * once the cap applies, so orders of 13+ pay slightly more than the ladder
 * would have charged. That is the intended trade for not enumerating forever.
 */
export const calculateTieredPricing = (totalQuantity) => {
  const qty = Number(totalQuantity) || 0;
  if (qty <= 0) return 0;

  if (isBulkDiscounted(qty)) {
    return roundToCents(
      qty * BASE_BOOK_PRICE * (1 - BULK_DISCOUNT_PERCENT / 100)
    );
  }

  return roundToCents(BASE_BOOK_PRICE + (qty - 1) * ADDITIONAL_BOOK_PRICE);
};

/** Upsell copy for the current order size, or null when the order is empty. */
export const getPromotionMessage = (totalQuantity) => {
  const qty = Number(totalQuantity) || 0;
  if (qty === 1) return `Add 2nd book for only $${ADDITIONAL_BOOK_PRICE}`;
  if (qty === 2) return `Add 3rd book for only $${ADDITIONAL_BOOK_PRICE}`;
  if (qty >= 3) return `Add another book for only $${ADDITIONAL_BOOK_PRICE}`;
  return null;
};

/**
 * Each printed book's share of the order price, so Review order can show why prices drop
 * (docs/design/HANDOFF.md, "Show savings when prices drop", decided 2026-10-26).
 *
 * `quantities`: the printed books' copy counts, in order. The first book carries the
 * BASE_BOOK_PRICE copy and every other copy is ADDITIONAL_BOOK_PRICE; at
 * BULK_DISCOUNT_MIN_QUANTITY+ copies every book is BULK_DISCOUNT_PERCENT off list instead.
 * The last book takes the rounding, so the books always add up to calculateTieredPricing.
 *
 * Returns { books: [{ price, list, discounted, reason }], list, total, savings, savingsLabel }:
 * `list` is copies x BASE_BOOK_PRICE, `reason` the short green line under a discounted book.
 */
export const priceOrderBooks = (quantities = []) => {
  const qtys = quantities.map((q) => Math.max(0, Number(q) || 0));
  const copies = qtys.reduce((sum, q) => sum + q, 0);
  const total = calculateTieredPricing(copies);
  const bulk = isBulkDiscounted(copies);
  const lastPriced = qtys.reduce((last, q, i) => (q > 0 ? i : last), -1);
  let running = 0;
  let firstSeen = false;
  const books = qtys.map((q, i) => {
    const list = roundToCents(q * BASE_BOOK_PRICE);
    if (!q) return { price: 0, list, discounted: false, reason: null };
    const first = !firstSeen;
    firstSeen = true;
    let price = bulk
      ? roundToCents(list * (1 - BULK_DISCOUNT_PERCENT / 100))
      : roundToCents(first ? BASE_BOOK_PRICE + (q - 1) * ADDITIONAL_BOOK_PRICE : q * ADDITIONAL_BOOK_PRICE);
    if (i === lastPriced) price = roundToCents(total - running);
    running = roundToCents(running + price);
    const reason = bulk
      ? `${BULK_DISCOUNT_PERCENT}% off ${BULK_DISCOUNT_MIN_QUANTITY}+ copies`
      : first
        ? `Extra copies $${ADDITIONAL_BOOK_PRICE} each`
        : q > 1
          ? `Additional book, $${ADDITIONAL_BOOK_PRICE} a copy`
          : "Additional book price";
    return { price, list, discounted: price < list - 0.001, reason };
  });
  const list = roundToCents(copies * BASE_BOOK_PRICE);
  const pricedBooks = qtys.filter((q) => q > 0).length;
  return {
    books,
    list,
    total,
    savings: roundToCents(list - total),
    savingsLabel: bulk
      ? `${BULK_DISCOUNT_MIN_QUANTITY}+ copies, ${BULK_DISCOUNT_PERCENT}% off`
      : pricedBooks > 1
        ? "Multi-book savings"
        : "Extra copy savings",
  };
};
